/**
 * LoanTeamTab Component
 *
 * Displays all people and roles associated with a loan:
 * - Internal Team (LO, LO2, LOA, Processor, Underwriter, Closer, File Starter)
 * - External Partners (Escrow Officer, Title Company, Investor, Broker)
 * - Originator / Compliance (NMLS)
 * - Milestone Activity timeline (who completed each stage)
 */

import type { ProspectLoan, MilestoneHistoryItem } from './types';
import { parseAsLocalDate } from '@/utils/dateHelpers';
import {
  User,
  Users,
  Building2,
  Landmark,
  Briefcase,
  Mail,
  Phone,
  CheckCircle2,
  Clock,
  Shield
} from 'lucide-react';

interface LoanTeamTabProps {
  loan: ProspectLoan;
}

// ── Visibility & display helpers ──────────────────────────────

function hasAssignment(...fields: (string | undefined | null)[]): boolean {
  return fields.some(f => f != null && f.trim() !== '');
}

function buildDisplayName(
  firstName?: string | null,
  lastName?: string | null,
  ...fallbacks: (string | undefined | null)[]
): string | null {
  const name = [firstName, lastName].filter(Boolean).join(' ').trim();
  if (name) {
return name;
}
  for (const fb of fallbacks) {
    if (fb && fb.trim()) {
return fb.trim();
}
  }
  return null;
}

function formatAddress(
  address?: string | null,
  city?: string | null,
  state?: string | null,
  zip?: string | null
): string | null {
  const parts = [address, [city, state].filter(Boolean).join(', '), zip].filter(Boolean);
  return parts.length > 0 ? parts.join(' ') : null;
}

function formatDate(dateStr?: string | null): string {
  if (!dateStr) {
return '—';
}
  try {
    return parseAsLocalDate(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  } catch {
    return '—';
  }
}

// ── Section header ────────────────────────────────────────────

function SectionHeader({
  title,
  icon: Icon
}: {
  title: string;
  icon: React.ElementType;
}) {
  return (
    <div className="flex items-center gap-3 bg-muted/50 px-4 py-3 rounded-lg border">
      <Icon className="h-5 w-5 text-muted-foreground" />
      <span className="font-semibold text-lg">{title}</span>
    </div>
  );
}

// ── Team member card ──────────────────────────────────────────

interface TeamCardProps {
  role: string;
  name: string;
  userId?: string | null;
  icon?: React.ElementType;
  children?: React.ReactNode;
}

function TeamCard({ role, name, userId, icon: Icon = User, children }: TeamCardProps) {
  return (
    <div className="flex items-start gap-3 p-4 rounded-lg border bg-card">
      <div className="flex items-center justify-center h-9 w-9 rounded-full bg-muted shrink-0">
        <Icon className="h-4 w-4 text-muted-foreground" />
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
          {role}
        </div>
        <div className="font-medium text-sm mt-0.5 truncate">{name}</div>
        {userId && (
          <div className="text-xs text-muted-foreground mt-0.5">{userId}</div>
        )}
        {children}
      </div>
    </div>
  );
}

// ── Main component ────────────────────────────────────────────

export function LoanTeamTab({ loan }: LoanTeamTabProps) {
  // ── Internal team definitions ──
  const internalTeam: {
    role: string;
    name: string | null;
    userId?: string | null;
    visible: boolean;
    icon?: React.ElementType;
  }[] = [
    {
      role: 'Loan Officer',
      name: buildDisplayName(loan.loan_officer_first_name, loan.loan_officer_last_name, loan.loan_officer_id),
      userId: loan.loan_officer_id,
      visible: true // Always shown
    },
    {
      role: 'Loan Officer 2',
      name: buildDisplayName(loan.loan_officer_2_first_name, loan.loan_officer_2_last_name, loan.loan_officer_2_id),
      userId: loan.loan_officer_2_id,
      visible: hasAssignment(loan.loan_officer_2_first_name, loan.loan_officer_2_last_name, loan.loan_officer_2_id)
    },
    {
      role: 'Loan Officer Assistant',
      name: buildDisplayName(loan.loan_officer_assistant_first_name, loan.loan_officer_assistant_last_name, loan.loan_officer_assistant_id),
      userId: loan.loan_officer_assistant_id,
      visible: hasAssignment(loan.loan_officer_assistant_first_name, loan.loan_officer_assistant_last_name, loan.loan_officer_assistant_id)
    },
    {
      role: 'Processor',
      name: buildDisplayName(loan.loan_processor_first_name, loan.loan_processor_last_name, loan.loan_processor_id),
      userId: loan.loan_processor_id,
      visible: hasAssignment(loan.loan_processor_first_name, loan.loan_processor_last_name, loan.loan_processor_id)
    },
    {
      role: 'Underwriter',
      name: buildDisplayName(loan.underwriter_first_name, loan.underwriter_last_name, loan.underwriter_name, loan.underwriter_id),
      userId: loan.underwriter_id,
      visible: hasAssignment(loan.underwriter_first_name, loan.underwriter_last_name, loan.underwriter_id, loan.underwriter_name)
    },
    {
      role: 'Closer',
      name: buildDisplayName(loan.closer_first_name, loan.closer_last_name, loan.closer_id),
      userId: loan.closer_id,
      visible: hasAssignment(loan.closer_first_name, loan.closer_last_name, loan.closer_id)
    },
    {
      role: 'File Starter',
      name: buildDisplayName(loan.file_starter_first_name, loan.file_starter_last_name, loan.file_starter_id),
      userId: loan.file_starter_id,
      visible: hasAssignment(loan.file_starter_first_name, loan.file_starter_last_name, loan.file_starter_id)
    }
  ];

  // ── Escrow officer visibility ──
  const hasEscrow = hasAssignment(loan.escrow_officer_name, loan.escrow_officer_email, loan.escrow_officer_phone);
  const escrowLabel = loan.escrow_officer_name ? 'Escrow Officer' : 'Escrow Contact';
  const escrowDisplayName = loan.escrow_officer_name || loan.escrow_officer_email || loan.escrow_officer_phone || '—';

  // ── Title company ──
  const hasTitleCompany = hasAssignment(loan.title_escrow_company_name);
  const titleAddress = formatAddress(
    loan.title_escrow_address,
    loan.title_escrow_city,
    loan.title_escrow_state,
    loan.title_escrow_zip
  );

  // ── Originator ──
  const hasOriginator = hasAssignment(loan.loan_originator_name, loan.nmls_loan_originator_id);

  // ── Milestone activity ──
  const milestones = loan.milestone_history ?? [];
  const visibleMilestones = milestones.filter(
    (m: MilestoneHistoryItem) =>
      (m.done === true && m.doneDate != null) ||
      (m.done !== true && m.startDate != null)
  );

  return (
    <div className="space-y-6">
      {/* Section A: Internal Team */}
      <div>
        <SectionHeader title="Internal Team" icon={Users} />
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 mt-4">
          {internalTeam
            .filter((t) => t.visible)
            .map((t) => (
              <TeamCard
                key={t.role}
                role={t.role}
                name={t.name || 'Unassigned'}
                userId={t.name && t.name !== t.userId ? t.userId : undefined}
                icon={t.icon}
              />
            ))}
        </div>
      </div>

      {/* Section B: External Partners */}
      {(hasEscrow || hasTitleCompany || hasAssignment(loan.investor_name) || hasAssignment(loan.broker_name)) && (
        <div>
          <SectionHeader title="External Partners" icon={Building2} />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 mt-4">
            {hasEscrow && (
              <TeamCard role={escrowLabel} name={escrowDisplayName} icon={User}>
                <div className="mt-2 space-y-1">
                  {loan.escrow_officer_email && (
                    <a
                      href={`mailto:${loan.escrow_officer_email}`}
                      className="flex items-center gap-1.5 text-xs text-primary hover:underline"
                    >
                      <Mail className="h-3 w-3" />
                      {loan.escrow_officer_email}
                    </a>
                  )}
                  {loan.escrow_officer_phone && (
                    <a
                      href={`tel:${loan.escrow_officer_phone}`}
                      className="flex items-center gap-1.5 text-xs text-primary hover:underline"
                    >
                      <Phone className="h-3 w-3" />
                      {loan.escrow_officer_phone}
                    </a>
                  )}
                </div>
              </TeamCard>
            )}

            {hasTitleCompany && (
              <TeamCard role="Title/Escrow Company" name={loan.title_escrow_company_name!} icon={Building2}>
                {titleAddress && (
                  <div className="text-xs text-muted-foreground mt-1">{titleAddress}</div>
                )}
              </TeamCard>
            )}

            {hasAssignment(loan.investor_name) && (
              <TeamCard role="Investor" name={loan.investor_name!} icon={Landmark} />
            )}

            {hasAssignment(loan.broker_name) && (
              <TeamCard role="Broker" name={loan.broker_name!} icon={Briefcase}>
                {formatAddress(loan.broker_address, loan.broker_city, loan.broker_state, loan.broker_zip) && (
                  <div className="text-xs text-muted-foreground mt-1">
                    {formatAddress(loan.broker_address, loan.broker_city, loan.broker_state, loan.broker_zip)}
                  </div>
                )}
              </TeamCard>
            )}
          </div>
        </div>
      )}

      {/* Section C: Originator / Compliance */}
      {hasOriginator && (
        <div>
          <SectionHeader title="Originator" icon={Shield} />
          <div className="flex items-center gap-4 mt-3 px-4 py-3 rounded-lg border bg-card text-sm">
            <div>
              <span className="text-muted-foreground">Originator: </span>
              <span className="font-medium">{loan.loan_originator_name || '—'}</span>
            </div>
            <div>
              <span className="text-muted-foreground">NMLS# </span>
              <span className="font-medium">{loan.nmls_loan_originator_id || '—'}</span>
            </div>
          </div>
        </div>
      )}

      {/* Section D: Milestone Activity */}
      <div>
        <SectionHeader title="Milestone Activity" icon={Clock} />
        {visibleMilestones.length === 0 ? (
          <p className="text-sm text-muted-foreground mt-3 px-4">
            No milestone data available
          </p>
        ) : (
          <div className="mt-4 space-y-1">
            {visibleMilestones.map((m: MilestoneHistoryItem, idx: number) => {
              const isCompleted = m.done === true && m.doneDate != null;
              return (
                <div
                  key={m.id || `${m.name}-${idx}`}
                  className="flex items-center gap-3 px-4 py-2.5 rounded-lg hover:bg-muted/30 transition-colors"
                >
                  {isCompleted ? (
                    <CheckCircle2 className="h-4 w-4 text-green-600 shrink-0" />
                  ) : (
                    <Clock className="h-4 w-4 text-amber-500 shrink-0" />
                  )}
                  <div className="flex-1 min-w-0">
                    <span className="font-medium text-sm">{m.name}</span>
                  </div>
                  <div className="text-xs text-muted-foreground text-right shrink-0">
                    {isCompleted ? (
                      <>
                        <span>Completed by {m.doneBy || '—'}</span>
                        <span className="mx-1.5">·</span>
                        <span>{formatDate(m.doneDate)}</span>
                      </>
                    ) : (
                      <>
                        <span>In Progress</span>
                        <span className="mx-1.5">·</span>
                        <span>Started {formatDate(m.startDate)}</span>
                      </>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
